import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import {
  Box,
  TextField,
  ListItem,
  ListItemButton,
  Checkbox,
  ListItemText,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useContext } from "react";
import { PlatformContext } from "./AddPromotions";
import { getPlatformBrands } from "../../redux/slices/promotions";

/**
 * BrandList component fetches and displays a list of brands
 *
 * @param {Function} changeHandler - Callback function to handle brand selection.
 * @param {Object} inputValue - Object/Array containing the currently selected brand details.
 */
export default function BrandList({
  changeHandler = () => {},
  inputValue = {},
  isShowAnyOption = false,
  listType = "",
  setAnchorEl,
  externalSearchTerm = "",
  disableInternalSearch = false,
}) {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({
    skip: 0,
    limit: 10,
    searchTerm: "",
  });
  const platform = useContext(PlatformContext);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [brandsList, setBrandsList] = useState([]);
  const [selectAllActive, setSelectAllActive] = useState(false);

  const observer = useRef();

  const searchTerm = disableInternalSearch ? externalSearchTerm : filter.searchTerm;

  const lastPostElementRef = useCallback(
    (node) => {
      if (isLoading || brandsList.length >= total) return;
      if (observer.current) observer.current.disconnect();
      const { limit } = filter;

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            const newSkip = filter.skip + limit;
            setIsLoading(true);

            dispatch(
              getPlatformBrands({
                skip: newSkip,
                limit,
                platform: platform,
                searchTerm: searchTerm || "",
              })
            )
              .unwrap()
              .then((res) => {
                const { data, total: newTotal } = res;
                const newBrands = data?.map((item) => ({
                  brandId: item?.brandId,
                  brandName: item?.brandName,
                  status: item?.status,
                }));
                setBrandsList((prev) => [...prev, ...newBrands]);
                setTotal(newTotal);
                setFilter((prev) => ({
                  ...prev,
                  skip: newSkip,
                }));
                if (isAllSelected) handleSelectAll();
              })
              .finally(() => setIsLoading(false));
          }
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0.1,
        }
      );

      if (node) observer.current.observe(node);
    },
    [isLoading, brandsList.length, total, filter, platform, dispatch, searchTerm]
  );

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      getPlatformBrands({
        skip: 0,
        limit: filter?.limit,
        platform: platform,
        searchTerm: searchTerm || "",
      })
    )
      .unwrap()
      .then((res) => {
        setIsLoading(false);

        const { data, total } = res;
        const brandData = data?.map((item) => {
          return {
            brandId: item?.brandId,
            brandName: item?.brandName,
            status: item?.status,
          };
        });
        setTotal(total);
        setBrandsList(brandData);
        setFilter((prev) => ({ ...prev, skip: 0 }));
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [platform, searchTerm]);

  useEffect(() => {
    if (!selectAllActive || !Array.isArray(inputValue)) return;

    const newSelections = brandsList.filter(
      (brand) => !inputValue.some((item) => item.brandId === brand.brandId)
    );

    if (newSelections.length > 0) {
      changeHandler({
        ruleValue: [...inputValue, ...newSelections],
      });
    }
  }, [brandsList.length]);

  const isAllSelected =
    Array.isArray(inputValue) &&
    brandsList.length > 0 &&
    brandsList.every((brand) => inputValue.some((item) => item.brandId === brand.brandId));

  const handleSelectAll = () => {
    if (!Array.isArray(inputValue)) return;

    if (selectAllActive) {
      // Clear only loaded brands
      const remaining = inputValue.filter(
        (item) => !brandsList.some((brand) => brand.brandId === item.brandId)
      );

      changeHandler({ ruleValue: remaining });
      setSelectAllActive(false);
    } else {
      // Select logic
      const merged = [
        ...inputValue,
        ...brandsList.filter((brand) => !inputValue.some((item) => item.brandId === brand.brandId)),
      ];

      changeHandler({ ruleValue: merged });
      setSelectAllActive(true);
    }
  };

  const Row = ({ index, style }) => {
    const brand = brandsList[index];

    const isChecked = Array.isArray(inputValue)
      ? inputValue.some((item) => item?.brandId === brand?.brandId)
      : inputValue?.brandId === brand?.brandId;

    return (
      <ListItem
        key={brand?.brandId}
        disablePadding
        style={style}
        ref={brandsList.length === index + 1 ? lastPostElementRef : null}
      >
        <ListItemButton
          onClick={() => {
            if (Array.isArray(inputValue)) {
              if (!isChecked) {
                changeHandler({ ruleValue: [...inputValue, brand] });
              } else {
                changeHandler({
                  ruleValue: inputValue.filter((item) => item?.brandId !== brand?.brandId),
                });
              }
            } else {
              if (typeof setAnchorEl === "function") setAnchorEl(null);
              changeHandler({
                ruleValue: isChecked ? inputValue : brand,
              });
            }
          }}
          sx={{
            p: 0,
            bgcolor: (_theme) => (isChecked ? "action.selected" : "inherit"),
          }}
          className="popover-btn-area"
        >
          {Array.isArray(inputValue) && (
            <Checkbox
              edge="start"
              checked={isChecked}
              tabIndex={-1}
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                if (e.target.checked) {
                  if (!isChecked) {
                    changeHandler({ ruleValue: [...inputValue, brand] });
                  }
                } else {
                  changeHandler({
                    ruleValue: inputValue.filter((item) => item?.brandId !== brand?.brandId),
                  });
                }
              }}
              style={{ margin: 0 }}
            />
          )}

          <ListItemText
            primary={
              <Typography variant="body2" color="#2E2E3A" fontWeight="semibold">
                {brand?.brandName}
              </Typography>
            }
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {/* Search Box */}
      {!disableInternalSearch && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search brands..."
            onChange={debounce((e) => {
              const searchValue = e.target.value;
              setFilter((prev) => ({ ...prev, searchTerm: searchValue, skip: 0 }));
            }, 500)}
          />
        </Box>
      )}

      {/* Empty State */}
      {!isLoading && brandsList?.length === 0 && (
        <Box sx={{ p: 3, transform: "scale(0.9)" }}>Data Not Found</Box>
      )}
      {Array.isArray(inputValue) && (
        <ListItem
          disablePadding
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1,
          }}
        >
          <ListItemButton onClick={handleSelectAll}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={inputValue.length > 0 && !isAllSelected}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={<Typography fontWeight={600}>Select All</Typography>} />
          </ListItemButton>
        </ListItem>
      )}

      <Box sx={{ maxHeight: 300, overflow: "auto" }}>
        {brandsList.map((_, index) => (
          <Row key={brandsList[index]?.brandId} index={index} />
        ))}
      </Box>
      {/* Loading State */}
      <>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} color="primary" />
          </Box>
        )}{" "}
      </>
    </Box>
  );
}